import { Receipt } from 'lucide-react';
import type { AlertDetailResponse } from '@/types/alerts';
import {
  EvidenceRow,
  HeadlineLine,
  MissingContextLine,
  RendererShell,
  formatDateTime,
  getNum,
  getStr,
} from './_shared';

/**
 * TOLL-4 — Toll-Only Day.
 *
 * "Vehicle paid one or more tolls on this date but had zero fuel
 * transactions and zero odometer movement." Strong signal for a card
 * being used at a toll plaza without the underlying vehicle ever
 * actually moving — i.e. the card travelled, the vehicle didn't.
 *
 * Evidence keys (verified from live fraud.alert sample):
 *   rule_name, site_name, toll_date, driver_name, site_reference,
 *   driver_reference, same_day_fuel_count, transaction_datetime,
 *   vehicle_registration, transaction_reference
 */
export default function TollOnlyDayRenderer(
  evidence: Record<string, unknown> | null | undefined,
  alert: AlertDetailResponse,
): JSX.Element {
  const tollDate = getStr(evidence, 'toll_date');
  const sameDayFuel = getNum(evidence, 'same_day_fuel_count') ?? 0;
  const vehicleReg =
    getStr(evidence, 'vehicle_registration') ?? alert.asset_token ?? null;
  const driverName = getStr(evidence, 'driver_name');
  const siteName = getStr(evidence, 'site_name');
  const txnTime = getStr(evidence, 'transaction_datetime');

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <Receipt className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-600" />
        <HeadlineLine>
          Vehicle {vehicleReg ?? '(unknown)'} paid tolls on{' '}
          {tollDate ?? 'this date'} but had{' '}
          <span className="text-red-700">{sameDayFuel} fuel transactions</span>{' '}
          and no recorded odometer movement.
        </HeadlineLine>
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Toll date">{tollDate ?? '—'}</EvidenceRow>
        <EvidenceRow label="Vehicle">
          <span className="font-mono text-xs">{vehicleReg ?? '—'}</span>
        </EvidenceRow>
        <EvidenceRow label="Driver">{driverName ?? '—'}</EvidenceRow>
        <EvidenceRow label="Toll site">{siteName ?? '—'}</EvidenceRow>
        <EvidenceRow label="Same-day fuel transactions">
          <span className="font-medium text-red-700">{sameDayFuel}</span>
        </EvidenceRow>
        <EvidenceRow label="Toll transaction at">
          {formatDateTime(txnTime)}
        </EvidenceRow>
      </div>

      {!tollDate && !vehicleReg && (
        <MissingContextLine message="Toll date and vehicle reference are missing — see raw evidence below." />
      )}
    </RendererShell>
  );
}
